/**
 * EmbedRewarded — Lightweight embed page for the desktop rewarded-ads strategy.
 *
 * Route: /embed/rewarded
 *
 * This page is loaded inside an <iframe> by the Facturas Simples Windows desktop
 * app while a free-tier user's invoice is being processed (~35 seconds). It:
 *  1. Renders a single AdSense ad unit (responsive, slot from env var).
 *  2. Shows a minimal "Actualizar a Pro" CTA.
 *  3. Sends postMessage events to window.parent so the desktop app can react:
 *       { type: 'FS_EMBED_READY' }              — page fully mounted
 *       { type: 'FS_AD_IMPRESSION' }             — adsbygoogle push completed
 *       { type: 'FS_UPGRADE_CTA_CLICKED' }       — user clicked Upgrade button
 *
 * Security:
 *  Azure ASWA staticwebapp.config.json restricts frame-ancestors to
 *  facturassimples.com domains + Tauri/Electron origins only.
 *
 * Slot configuration:
 *  Set REACT_APP_ADSENSE_REWARDED_SLOT in Azure app settings once the AdSense
 *  console slot is created. Procedure: docs/ADSENSE_REWARDED_SETUP_PROCEDURE.md
 */

import React, { useEffect } from 'react';

// Slot ID: read from env var, fall back to placeholder until real slot is created.
const REWARDED_SLOT_ID =
  process.env.REACT_APP_ADSENSE_REWARDED_SLOT || '0000000000';

const PUBLISHER_ID = 'ca-pub-0726149070503281';

function postToParent(type, payload = {}) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type, ...payload }, '*');
    }
  } catch (e) {
    // Cross-origin parent — message still delivers; ignore read errors.
  }
}

export default function EmbedRewarded() {
  useEffect(() => {
    // Signal that the embed page has mounted and is ready.
    postToParent('FS_EMBED_READY');

    // Push the AdSense unit and signal impression attempt.
    try {
      if (typeof window !== 'undefined') {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        // adsbygoogle does not expose a reliable load callback; we fire the
        // impression event immediately after push. The desktop app should
        // still enforce its own minimum countdown (DESKTOP_REWARDED_DURATION_MS).
        postToParent('FS_AD_IMPRESSION');
      }
    } catch (e) {
      console.debug('AdSense load skipped:', e.message);
    }
  }, []);

  const handleUpgradeClick = () => {
    postToParent('FS_UPGRADE_CTA_CLICKED');
    // Also attempt to open the app's subscription modal via deep link
    // (the desktop app listens for the postMessage above and handles it).
  };

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: '#f8fafc',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        boxSizing: 'border-box',
        margin: 0,
      }}
    >
      {/* Minimal branding */}
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: '#1e40af',
            letterSpacing: '-0.3px',
          }}
        >
          Facturas Simples
        </span>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 12,
            color: '#64748b',
          }}
        >
          Mientras se procesa tu factura&hellip;
        </p>
      </div>

      {/* AdSense ad unit */}
      <div
        style={{
          width: '100%',
          maxWidth: 728,
          minHeight: 90,
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <ins
          className="adsbygoogle"
          style={{ display: 'block', width: '100%', maxWidth: 728, height: 90 }}
          data-ad-client={PUBLISHER_ID}
          data-ad-slot={REWARDED_SLOT_ID}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>

      {/* Upgrade CTA */}
      <button
        onClick={handleUpgradeClick}
        testID="embed-upgrade-cta"
        aria-label="Actualizar a Facturas Simples Pro para omitir la espera"
        style={{
          background: '#1e40af',
          color: '#ffffff',
          border: 'none',
          borderRadius: 10,
          padding: '12px 28px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: '-0.2px',
          boxShadow: '0 2px 8px rgba(30,64,175,0.25)',
        }}
      >
        Actualizar a Pro &mdash; sin esperas
      </button>

      <p
        style={{
          marginTop: 10,
          fontSize: 11,
          color: '#94a3b8',
          textAlign: 'center',
        }}
      >
        Publicidad &bull; Facturas Simples Lite
      </p>
    </div>
  );
}
